/**
 * Centralized Feature Flags and Limits Configuration
 * 
 * Controls what features are available to different user tiers and environments.
 */

import type { Environment } from './environments';

export interface FeatureConfig {
  // Core functionality
  localVaults: boolean;
  cloudSync: boolean;
  webAccess: boolean;
  
  // Storage limits
  maxLocalVaults: number;
  maxCloudVaults: number;
  maxItemsPerVault: number;
  maxVaultNameLength: number;
  
  // File handling
  thumbnailGeneration: boolean;
  maxThumbnailSizeKB: number;
  maxNoteSizeKB: number;
  
  // UI features
  dragAndDrop: boolean;
  contextMenus: boolean;
  bulkOperations: boolean;
  advancedSearch: boolean;
  customTags: boolean;

  // UI display limits
  // NOTE: These limits control how many bookmarks/notes are shown in the extension popup
  // Edit these values to adjust the display limits across all popup views
  popupMaxItems: number; // Maximum items shown in popup (default view)
  popupMaxItemsSearch: number; // Maximum items shown when searching
  
  // Security features
  biometricUnlock: boolean;
  sessionTimeout: number; // minutes, 0 = no timeout
  autoLock: boolean;
  
  // Import/Export
  importBookmarks: boolean;
  exportData: boolean;
  bulkImport: boolean;
  
  // Premium features
  prioritySupport: boolean;
  advancedAnalytics: boolean;
  customThemes: boolean;
  
  // Development features
  developerMode: boolean;
  debugLogs: boolean;
  performanceMetrics: boolean;
}

export type UserTier = 'free' | 'premium';

// Feature configurations by tier
const TIER_FEATURES: Record<UserTier, FeatureConfig> = {
  free: {
    // Core functionality
    localVaults: true,
    cloudSync: false,
    webAccess: false,

    // Storage limits
    maxLocalVaults: 1000000, // Effectively unlimited for local storage
    maxCloudVaults: 0,
    maxItemsPerVault: 1000000, // Effectively unlimited for local storage
    maxVaultNameLength: 100,

    // File handling
    thumbnailGeneration: true,
    maxThumbnailSizeKB: 100,
    maxNoteSizeKB: 1024, // 1MB

    // UI features
    dragAndDrop: true,
    contextMenus: true,
    bulkOperations: true,
    advancedSearch: true,
    customTags: true,

    // UI display limits
    popupMaxItems: 10000, // Show 10000 items in popup by default
    popupMaxItemsSearch: 50, // Show up to 50 items when searching

    // Security features
    biometricUnlock: false,
    sessionTimeout: 60, // 1 hour
    autoLock: true,
    
    // Import/Export
    importBookmarks: true,
    exportData: true,
    bulkImport: false,
    
    // Premium features
    prioritySupport: false,
    advancedAnalytics: false,
    customThemes: false,
    
    // Development features
    developerMode: false,
    debugLogs: false,
    performanceMetrics: false
  },
  
  premium: {
    // Core functionality
    localVaults: true,
    cloudSync: true,
    webAccess: true,

    // Storage limits
    maxLocalVaults: 1000000, // Effectively unlimited
    maxCloudVaults: 100,
    maxItemsPerVault: 10000,
    maxVaultNameLength: 100,

    // File handling
    thumbnailGeneration: true,
    maxThumbnailSizeKB: 100,
    maxNoteSizeKB: 1024, // 1MB

    // UI features
    dragAndDrop: true,
    contextMenus: true,
    bulkOperations: true,
    advancedSearch: true,
    customTags: true,

    // UI display limits
    popupMaxItems: 10000, // Show 10000 items in popup by default
    popupMaxItemsSearch: 50, // Show up to 50 items when searching

    // Security features
    biometricUnlock: true,
    sessionTimeout: 0, // No timeout for premium users
    autoLock: true,
    
    // Import/Export
    importBookmarks: true,
    exportData: true,
    bulkImport: true,
    
    // Premium features
    prioritySupport: true,
    advancedAnalytics: true,
    customThemes: true,
    
    // Development features
    developerMode: false,
    debugLogs: false,
    performanceMetrics: false
  }
};

// Environment-specific feature overrides
const ENVIRONMENT_OVERRIDES: Record<Environment, Partial<FeatureConfig>> = {
  local: {
    developerMode: true,
    debugLogs: true,
    performanceMetrics: true,
    sessionTimeout: 0, // No timeout in local development
  },
  
  dev: {
    developerMode: true,
    debugLogs: true,
    performanceMetrics: true,
  },
  
  prod: {
    developerMode: false,
    debugLogs: false,
    performanceMetrics: false,
  }
};

/**
 * Get feature configuration for a specific user tier
 */
export function getFeaturesForTier(tier: UserTier): FeatureConfig {
  return { ...TIER_FEATURES[tier] };
}

/**
 * Get feature configuration with environment overrides applied
 */
export function getFeaturesForTierAndEnvironment(tier: UserTier, environment: Environment): FeatureConfig {
  const baseFeatures = getFeaturesForTier(tier);
  const environmentOverrides = ENVIRONMENT_OVERRIDES[environment];
  
  return {
    ...baseFeatures,
    ...environmentOverrides
  };
}

/**
 * Check if a specific feature is enabled for a tier
 */
export function isFeatureEnabled(feature: keyof FeatureConfig, tier: UserTier, environment?: Environment): boolean {
  const config = environment 
    ? getFeaturesForTierAndEnvironment(tier, environment)
    : getFeaturesForTier(tier);
  
  return Boolean(config[feature]);
}

/**
 * Get storage limit for a specific tier
 */
export function getStorageLimit(limitType: 'maxLocalVaults' | 'maxCloudVaults' | 'maxItemsPerVault', tier: UserTier): number {
  const config = getFeaturesForTier(tier);
  return config[limitType];
}

/**
 * Feature flags that can be toggled dynamically (future use)
 */
export interface DynamicFeatureFlags {
  // Experimental features
  enableNewVaultEditor: boolean;
  enableAdvancedEncryption: boolean;
  enableMobileApp: boolean;
  
  // A/B testing features
  enableNewOnboarding: boolean;
  enableImprovedSearch: boolean;
  enableSocialSharing: boolean;
  
  // Rollout features
  enableWebApp: boolean;
  enableCloudSync: boolean;
  enableStripePayments: boolean;
}

// Default dynamic feature flags (can be overridden via environment variables or remote config)
export const DEFAULT_DYNAMIC_FEATURES: DynamicFeatureFlags = {
  // Experimental features
  enableNewVaultEditor: false,
  enableAdvancedEncryption: false,
  enableMobileApp: false,
  
  // A/B testing features
  enableNewOnboarding: true,
  enableImprovedSearch: true,
  enableSocialSharing: false,
  
  // Rollout features
  enableWebApp: true,
  enableCloudSync: true,
  enableStripePayments: true,
};

/**
 * Get dynamic feature flags (with environment variable overrides)
 */
export function getDynamicFeatures(): DynamicFeatureFlags {
  const features = { ...DEFAULT_DYNAMIC_FEATURES };
  
  // Allow environment variable overrides
  if (typeof process !== 'undefined' && process.env) {
    // Convert environment variables to feature flags
    Object.keys(features).forEach(key => {
      const envKey = `SAFERMARK_FEATURE_${key.replace(/([A-Z])/g, '_$1').toUpperCase()}`;
      const envValue = process.env[envKey];
      if (envValue !== undefined) {
        (features as any)[key] = envValue.toLowerCase() === 'true';
      }
    });
  }
  
  return features;
}

/**
 * Check if a dynamic feature is enabled
 */
export function isDynamicFeatureEnabled(feature: keyof DynamicFeatureFlags): boolean {
  const features = getDynamicFeatures();
  return features[feature];
}

// Rate limiting configuration
export interface RateLimits {
  // API rate limits (requests per window)
  authRequests: { windowMs: number; max: number };
  vaultOperations: { windowMs: number; max: number };
  generalApi: { windowMs: number; max: number };
  
  // Extension-specific limits
  thumbnailGeneration: { windowMs: number; max: number };
  cloudSyncAttempts: { windowMs: number; max: number };
  
  // User-specific limits
  accountCreation: { windowMs: number; max: number };
  passwordResets: { windowMs: number; max: number };
}

export const RATE_LIMITS: RateLimits = {
  authRequests: {
    windowMs: 15 * 60 * 1000, // 15 minutes
    max: 10 // 10 auth requests per 15 minutes
  },
  
  vaultOperations: {
    windowMs: 60 * 1000, // 1 minute
    max: 100 // 100 vault operations per minute
  },
  
  generalApi: {
    windowMs: 15 * 60 * 1000, // 15 minutes
    max: 1000 // 1000 API requests per 15 minutes
  },
  
  thumbnailGeneration: {
    windowMs: 60 * 1000, // 1 minute
    max: 20 // 20 thumbnails per minute
  },
  
  cloudSyncAttempts: {
    windowMs: 5 * 60 * 1000, // 5 minutes
    max: 10 // 10 sync attempts per 5 minutes
  },
  
  accountCreation: {
    windowMs: 60 * 60 * 1000, // 1 hour
    max: 3 // 3 account creations per hour per IP
  },
  
  passwordResets: {
    windowMs: 60 * 60 * 1000, // 1 hour
    max: 5 // 5 password resets per hour per email
  }
};

// Export constants
export { TIER_FEATURES, ENVIRONMENT_OVERRIDES };